'use client';

import * as React from 'react';
import Link from 'next/link';

import { APP_DESCRIPTION, APP_NAME } from '@workspace/common/app';
import { Logo } from '@workspace/ui/components/logo';
import { Separator } from '@workspace/ui/components/separator';
import { ThemeSwitcher } from '@workspace/ui/components/theme-switcher';

import { FOOTER_LINKS, SOCIAL_LINKS } from '~/components/marketing-links';
import { NewsletterSignupForm } from '~/components/newsletter-signup-form';
import { useMarketingAnalytics } from '~/hooks/use-marketing-analytics';

export function Footer(): React.JSX.Element {
  const { trackDiscordClick, trackDocsClick, trackGithubClick } =
    useMarketingAnalytics();

  return (
    <footer className="px-2 pb-10 pt-20 sm:container">
      <h2 className="sr-only">Footer</h2>
      <div className="container">
        <div className="flex flex-col items-center gap-6 text-center">
          <Logo />
          <p className="max-w-xl text-sm text-muted-foreground">
            {APP_DESCRIPTION}
          </p>
          <nav aria-label="Footer">
            <ul className="flex flex-wrap items-center justify-center gap-4">
              {FOOTER_LINKS.map((link) => (
                <li key={link.name}>
                  <Link
                    href={link.href}
                    title={link.name}
                    onClick={() => {
                      if (link.name === 'Docs') {
                        trackDocsClick('footer_nav_docs', link.href);
                      }
                    }}
                    className="text-sm text-muted-foreground transition-colors hover:text-foreground"
                  >
                    {link.name}
                  </Link>
                </li>
              ))}
            </ul>
          </nav>
        </div>
        <div className="mt-10 rounded-[1.75rem] border border-border/70 bg-muted/20 p-6 md:p-8">
          <div className="flex flex-col gap-5 lg:flex-row lg:items-end lg:justify-between">
            <div className="max-w-xl space-y-2 text-left">
              <h3 className="text-lg font-semibold text-foreground">
                Sign up for the newsletter
              </h3>
              <p className="text-sm leading-relaxed text-muted-foreground">
                Product updates, launch notes, and community signals from the
                Viventium team.
              </p>
            </div>
            <NewsletterSignupForm
              inputId="footer-newsletter-email"
              source="footer_newsletter_form"
              className="w-full max-w-md"
            />
          </div>
        </div>
        <div className="mt-8 border-t pt-8">
          <div className="flex flex-col items-center justify-between gap-4 sm:flex-row">
            <p className="text-sm text-muted-foreground">
              © {new Date().getFullYear()} {APP_NAME}. All rights reserved.
            </p>
            <div className="flex flex-row flex-wrap items-center justify-center gap-3">
              {SOCIAL_LINKS.map((link) => (
                <Link
                  key={link.name}
                  title={link.name}
                  href={link.href}
                  onClick={() => {
                    if (link.name === 'GitHub') {
                      trackGithubClick('footer_social_github');
                    }

                    if (link.name === 'Discord') {
                      trackDiscordClick('footer_social_discord');
                    }
                  }}
                  className="text-muted-foreground transition-colors hover:text-foreground"
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  <span className="sr-only">{link.name}</span>
                  {link.icon}
                </Link>
              ))}
              <Separator
                orientation="vertical"
                className="h-4"
              />
              <ThemeSwitcher />
            </div>
          </div>
        </div>
      </div>
    </footer>
  );
}
